import { formatCurrency } from '@fourthwall/utils/dist/currency';
import uniq from 'lodash-es/uniq';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCostsPerSize, getSameSizesHaveDifferentCosts } from '../store/product/utils';
import { getHasDifferentSizes, getInitialPricingType } from '../utils';
import { useOffer } from './useOffer';
export const useProductPricing = () => {
    const [pricingType, setPricingType] = useState('IMPLICIT');
    const { productId = '' } = useParams();
    const { offer } = useOffer(productId);
    const isCreatorDeclaredCostEnabled = offer.manufacturingService === 'MANUFACTURED_BY_CREATOR' &&
        typeof offer.metafields === 'object' &&
        offer.metafields !== null &&
        'declareCreatorCost' in offer.metafields &&
        Boolean(offer.metafields.declareCreatorCost);
    const profits = ((variants) => {
        return variants.reduce((result, currentVariant) => {
            const cost = currentVariant.cost?.value || null;
            if (cost) {
                const price = currentVariant.price.value;
                return [...result, Number(formatCurrency(price - cost))];
            }
            return result;
        }, []);
    })(offer.variants);
    const doProfitsVary = uniq(profits).length > 1;
    const doCompareAtPricesVary = (() => {
        if (offer.variants.every((variant) => variant.compareAtPrice === null))
            return false;
        const baseCompareAtPrice = offer.variants[0].compareAtPrice?.value || 0;
        const basePrice = offer.variants[0].price.value;
        return offer.variants.some((variant) => (variant.compareAtPrice?.value || 0) - variant.price.value !==
            baseCompareAtPrice - basePrice);
    })();
    const costsPerSize = getCostsPerSize(offer.variants);
    const sameSizesHaveDifferentCosts = getSameSizesHaveDifferentCosts(costsPerSize);
    const sizeVariant = offer.variantTypes.find((variant) => variant.type === 'SIZE');
    const hasDifferentSizes = getHasDifferentSizes(sizeVariant);
    const initialPricingType = getInitialPricingType({
        doProfitsVary,
        hasDifferentSizes,
        sameSizesHaveDifferentCosts,
        doCompareAtPricesVary,
    });
    useEffect(() => {
        if (initialPricingType !== pricingType) {
            setPricingType(initialPricingType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPricingType]);
    const showPricingPerVariant = useMemo(() => {
        if (isCreatorDeclaredCostEnabled && offer.variants.length > 1) {
            return true;
        }
        return offer.variantTypes.some((variantType) => {
            // per size pricing is not supposed to be displayed in variants table
            if (variantType.type === 'SIZE')
                return false;
            // should not show per variant pricing if there is only one variant option anyway
            if (variantType.options.length < 2)
                return false;
            if (isCreatorDeclaredCostEnabled) {
                return true;
            }
            // if pricing is enabled for this variant type
            return variantType.variesBy.price;
        });
    }, [offer.variantTypes, isCreatorDeclaredCostEnabled]);
    return {
        pricingType,
        showPricingPerVariant,
        setPricingType,
    };
};
